import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import Select from "react-select"
import makeAnimated from "react-select/animated"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { usePermissions } from "API/PermissionContext"

const animatedComponents = makeAnimated()
const allDisplayParams = [
  { label: "Bill Date", value: "billDate" },
  { label: "Bill Period", value: "billPeriod" },
  { label: "Company", value: "company" },
  { label: "Plant", value: "plant" },
  { label: "Chilling Center", value: "ccName" },
  { label: "Route", value: "routeName" },
  { label: "Center", value: "centerName" },
  { label: "Producer", value: "producer" },
  { label: "Quantity - Liters", value: "qtyLtrs" },
  { label: "Quantity - KG", value: "qtyKg" },
  { label: "Avg. FAT", value: "avgFat" },
  { label: "Avg. SNF", value: "avgSnf" },
  { label: "Inc. Amt", value: "incAmt" },
  { label: "Proc. Amt Without Inc.", value: "procAmtNoInc" },
  { label: "Proc. Amt", value: "procAmt" },
  { label: "Adv. Total", value: "advTotal" },
  { label: "Adv. Opening", value: "advOpening" },
  { label: "Adv. Deduction", value: "advDeduction" },
  { label: "Adv. Closing", value: "advClosing" },
  { label: "Loan Total", value: "loanTotal" },
  { label: "Loan Opening", value: "loanOpening" },
  { label: "Loan Deduction", value: "loanDeduction" },
  { label: "Loan Closing", value: "loanClosing" },
  { label: "Mat. Total", value: "matTotal" },
  { label: "Mat. Opening", value: "matOpening" },
  { label: "Mat. Deduction", value: "matDeduction" },
  { label: "Mat. Closing", value: "matClosing" },
  { label: "Rec. Total", value: "recTotal" },
  { label: "Rec. Opening", value: "recOpening" },
  { label: "Rec. Deduction", value: "recDeduction" },
  { label: "Addl. Payout", value: "addlPayout" },
  { label: "Rec. Closing", value: "recClosing" },
  { label: "Fund Deduction", value: "fundDeduction" },
  { label: "Bal. Amount", value: "balAmount" },
  { label: "Payment Mode", value: "paymentMode" },
]

const ProcurementBillSummary = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [summaryType, setSummaryType] = useState("1")
  const [allCenters, setAllCenters] = useState([])
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [companies, setCompanies] = useState([])
  const [vendors, setVendors] = useState([])
  const [selectedParams, setSelectedParams] = useState([allDisplayParams.find(p => p.value === "producer")])

  useEffect(() => { loadData() }, [])
  const loadData = async () => {
    try {
      const [cResp, pResp, coResp, vResp] = await Promise.all([api.get("centers"), api.get("plants"), api.get("companies"), api.get("vendors")])
      const ccData = Array.isArray(cResp.data.data) ? cResp.data.data : cResp.data.data?.data || []
      setAllCenters(ccData)
      setCenters(ccData)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData)
      setPlants(pData)
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
      setVendors(Array.isArray(vResp.data.data) ? vResp.data.data : vResp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }
  const companyChange = async e => { const val = e.target.value; setCenters(allCenters); setRoutes([]); if (val && val !== "") { try { const r = await api.get("plants-by-company/" + val); setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){} } else setPlants(allPlants) }
  const plantChange = async e => { const val = e.target.value; setRoutes([]); if (val && val !== "") { try { const r = await api.get("chillings-by-plant/" + val); setCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){} } else setCenters(allCenters) }
  const ccChange = async e => { try { const u = !e.target.value || e.target.value == "0" ? "routes" : "route-cc/" + e.target.value; const r = await api.get(u); setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){} }

  const selectAll = () => setSelectedParams(allDisplayParams)
  const deselectAll = () => setSelectedParams([])
  const pVals = selectedParams.map(p => p.value)

  const handleSubmit = async (e, v) => {
    setLoading(true)
    try {
      const resp = await api.post("reports/procurement-bill-summary", v)
      if (resp.data.success) { setResults(resp.data.data || []); setSubmitted(true) }
    } catch (err) { console.error(err) }
    setLoading(false)
  }
  const downloadPdf = () => { const pdf = new jsPDF("l"); pdf.autoTable({ html: "#billSummaryTbl" }); pdf.save("Procurement-Bill-Summary.pdf") }

  const getVal = (r, key) => {
    const v = r[key]
    if (v === undefined || v === null) return "—"
    if (typeof v === "number") return v.toFixed(2)
    return v
  }

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint"><Row><Col md={12}><h4>Procurement Bill Summary</h4></Col></Row></div>
      <div className="dashContent">
        <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
          <Row>
            <Col md={3}><div className="mb-3"><AvRadioGroup inline name="summaryType" label="Summary Type" value="1" onChange={e => setSummaryType(e.target.value)}><AvRadio label="Vendor Payment" value="1" /><AvRadio label="Producer Payment" value="2" /></AvRadioGroup></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All</option><option value="0">All</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
          </Row>
          <Row>
            <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route"><option value="">All</option><option value="0">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
            {summaryType == "1" ? (
              <Col md={3}><div className="mb-3"><AvField type="select" name="vendor" label="Vendor"><option value="">All</option>{vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName}</option>)}</AvField></div></Col>
            ) : (
              <Col md={3}><div className="mb-3"><AvField type="select" name="vendor" label="Vendor"><option value="">All</option>{vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName}</option>)}</AvField></div></Col>
            )}
            <Col md={3}><div className="mb-3"><AvField name="billDateFrom" label="Bill Date From *" type="date" required /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="billDateTo" label="Bill Date To *" type="date" required /></div></Col>
          </Row>
          <Row>
            <Col md={3}><div className="mb-3"><AvRadioGroup inline name="paymentMode" label="Payment Mode" value="0"><AvRadio label="All" value="0" /><AvRadio label="Cash" value="1" /><AvRadio label="Bank" value="2" /></AvRadioGroup></div></Col>
            <Col md={3}><div className="mb-3"><AvRadioGroup inline name="paymentAmount" label="Payment Amount *" value="0"><AvRadio label="All" value="0" /><AvRadio label="Payment Available" value="1" /><AvRadio label="No Payment" value="2" /></AvRadioGroup></div></Col>
            <Col md={6}><div className="mb-3 templating-select select2-container"><label className="control-label">Display Parameters</label><div className="d-flex mb-1"><button type="button" className="btn btn-outline-primary btn-sm me-1" onClick={selectAll}>Select All</button><button type="button" className="btn btn-outline-secondary btn-sm" onClick={deselectAll}>Deselect All</button></div><Select value={selectedParams} isMulti onChange={e => setSelectedParams(e || [])} options={allDisplayParams} classNamePrefix="select2-selection" closeMenuOnSelect={false} components={animatedComponents} /></div></Col>
          </Row>
          <div className="mb-3 mt-2 text-center"><button className="btn btn-primary w-md" type="submit" disabled={loading}>{loading ? <><Spinner size="sm" /> Submitting...</> : <><i className="fas fa-check me-1"></i>Submit</>}</button></div>
        </AvForm>
        {submitted && <>
          {canExport(MID) && <div className="exportBtns notToPrint mb-2"><button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>Download PDF</button><button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button></div>}
          <div className="table-responsive">
            <Table id="billSummaryTbl" className="dairyTbl" size="sm" bordered>
              <thead><tr>
                <th>S.No</th>
                {pVals.map((col, i) => { const p = allDisplayParams.find(x => x.value === col); return <th key={i}>{p ? p.label : col}</th> })}
                {pVals.length === 0 && <><th>Bill Date</th><th>Route</th><th>Proc. Amt</th><th>Bal. Amount</th></>}
              </tr></thead>
              <tbody>
                {results.length > 0 ? results.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    {pVals.map((col, j) => <td key={j}>{getVal(r, col)}</td>)}
                    {pVals.length === 0 && <><td>{r.billDate}</td><td>{r.routeName}</td><td>{r.procAmt}</td><td>{r.balAmount}</td></>}
                  </tr>
                )) : <tr><td colSpan="30" className="text-center py-3">No Results Found</td></tr>}
              </tbody>
              {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
                <td>TOTAL</td>
                {pVals.map((col, j) => {
                  const numCols = ["qtyLtrs","qtyKg","avgFat","avgSnf","incAmt","procAmtNoInc","procAmt","advTotal","advOpening","advDeduction","advClosing","loanTotal","loanOpening","loanDeduction","loanClosing","matTotal","matOpening","matDeduction","matClosing","recTotal","recOpening","recDeduction","addlPayout","recClosing","fundDeduction","balAmount"]
                  if (numCols.includes(col)) {
                    const total = results.reduce((s, r) => s + parseFloat(r[col] || 0), 0)
                    return <td key={j}>{total.toFixed(2)}</td>
                  }
                  return <td key={j}>—</td>
                })}
                {pVals.length === 0 && <><td>—</td><td>—</td><td>{results.reduce((s, r) => s + parseFloat(r.procAmt || 0), 0).toFixed(2)}</td><td>{results.reduce((s, r) => s + parseFloat(r.balAmount || 0), 0).toFixed(2)}</td></>}
              </tr></tfoot>}
            </Table>
          </div>
        </>}
      </div>
    </div>
  )
}
export default ProcurementBillSummary
